import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { rejectReprintRequest } from "../../services";
import { ReprintRequestRow } from "../../types/reprintRequest.type";
import { CHARCOAL, getRequestedCopiesLabel } from "./data";

// BE: review_note is `{ type: STRING, maxLength: 255 }` — keep the input bounded
// so a long note fails in the field rather than as a 400 from the server.
const REVIEW_NOTE_MAX = 255;

interface RejectDialogProps {
  open: boolean;
  request: ReprintRequestRow | null;
  onClose: () => void;
  onRejected: () => void;
}

export default function RejectDialog({
  open,
  request,
  onClose,
  onRejected,
}: RejectDialogProps) {
  const [loading, setLoading] = useState<boolean>(false);
  // Optional. Unlike the revoke flow, this note IS persisted (review_note).
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const handleReject = async () => {
    if (!request) return;
    setLoading(true);
    try {
      await rejectReprintRequest(request.uuid, note.trim() || undefined);
      onRejected();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, color: CHARCOAL }}>
        ปฏิเสธคำขอพิมพ์ซ้ำ?
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          พนักงานขายจะไม่ได้รับสิทธิ์พิมพ์บิลนี้ซ้ำ การกระทำนี้จะถูกบันทึกใน audit log
        </Typography>
        {request && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {request.transaction_no || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {request.customer_name || "-"} · {request.customer_code || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ผู้ขอ: {request.requested_by_name || "-"} ·{" "}
              {getRequestedCopiesLabel(request.requested_copies)}
            </Typography>
          </Box>
        )}
        <TextField
          label="เหตุผลการปฏิเสธ (ไม่บังคับ)"
          placeholder="ระบุเหตุผลให้พนักงานขายทราบ"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={{ mt: 2 }}
          slotProps={{ htmlInput: { maxLength: REVIEW_NOTE_MAX } }}
          helperText={`${note.length}/${REVIEW_NOTE_MAX}`}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        <Button
          onClick={handleReject}
          variant="contained"
          color="error"
          loading={loading}
        >
          ยืนยันปฏิเสธ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
